import type {
  ActionDecision,
  AssistantAction,
  AssistantMessage,
  AssistantQuestion,
} from './types';

/*
 * Pure transcript state + reducer for the assistant. Kept free of hooks/IO so
 * it is easy to reason about and test in isolation; AssistantProvider owns the
 * wiring (REST/websocket) and dispatches these actions.
 *
 * Messages are keyed by `external_id` (server) or `client_message_id`
 * (optimistic) and ordered by monotonic `seq`. Optimistic messages get the
 * OPTIMISTIC_SEQ sentinel and are re-seq'd on insert so they stay chronological.
 */

export interface State {
  messages: AssistantMessage[];
  turnActive: boolean;
}

export const initialState: State = { messages: [], turnActive: false };

export type Action =
  | { t: 'reset' }
  | { t: 'setMessages'; messages: AssistantMessage[] }
  | { t: 'upsert'; message: AssistantMessage }
  | { t: 'addUser'; message: AssistantMessage }
  | { t: 'markQueuedSent'; clientMessageId: string }
  | { t: 'removeByClientId'; clientMessageId: string }
  | { t: 'delta'; messageId: string; delta: string }
  | { t: 'complete'; messageId?: string; status?: AssistantMessage['status'] }
  | { t: 'fail'; messageId?: string; error?: string }
  | {
      t: 'toolCall';
      messageId: string;
      toolCall: AssistantMessage['tool_calls'][number];
    }
  | { t: 'question'; messageId: string; question: AssistantQuestion }
  | { t: 'action'; messageId?: string; action: AssistantAction }
  | {
      t: 'viewCommand';
      messageId?: string;
      command: NonNullable<AssistantMessage['view_command']>;
    }
  | { t: 'actionDecided'; actionId: string; decision: ActionDecision | null }
  | {
      t: 'questionAnswered';
      messageId: string;
      selected: string[];
      answeredBy?: string;
    }
  | { t: 'setTurnActive'; value: boolean };

/** Sentinel seq for optimistic (not-yet-persisted) messages. */
export const OPTIMISTIC_SEQ = Number.MAX_SAFE_INTEGER;

const sortBySeq = (a: AssistantMessage, b: AssistantMessage) => {
  if (a.seq !== b.seq) return a.seq - b.seq;
  // equal seq → keep insertion order (Array.sort is stable in V8)
  return 0;
};

// Highest real (server-assigned) seq currently in the list, ignoring the
// placeholder seq used for not-yet-persisted optimistic messages.
const maxRealSeq = (list: AssistantMessage[]) =>
  list.reduce(
    (mx, m) => (m.seq < OPTIMISTIC_SEQ ? Math.max(mx, m.seq) : mx),
    0
  );

function mergeMessage(
  prev: AssistantMessage,
  incoming: AssistantMessage
): AssistantMessage {
  // Preserve the answered state of a question if a re-sent copy omits it.
  let question = incoming.question ?? prev.question;
  if (incoming.question && prev.question) {
    question = {
      ...incoming.question,
      answered_by:
        incoming.question.answered_by ?? prev.question.answered_by ?? null,
      selected_options: incoming.question.selected_options?.length
        ? incoming.question.selected_options
        : prev.question.selected_options,
    };
  }
  // Merge action lists by external_id, preserving any optimistic confirm/reject
  // decision when a re-sent copy omits it.
  const prevActions = prev.actions ?? [];
  const incomingActions = incoming.actions ?? [];
  const actions = incomingActions.length
    ? incomingActions.map((a) => {
        const old = prevActions.find((p) => p.external_id === a.external_id);
        return old ? { ...a, decision: a.decision ?? old.decision ?? null } : a;
      })
    : prevActions;
  return {
    ...prev,
    ...incoming,
    // keep already-streamed content if the incoming one is empty (delta-built)
    content: incoming.content || prev.content,
    tool_calls: incoming.tool_calls?.length
      ? incoming.tool_calls
      : prev.tool_calls,
    question,
    actions,
    view_command: incoming.view_command ?? prev.view_command,
    streaming: false,
    queued: false,
    optimistic: false,
  };
}

function upsert(
  list: AssistantMessage[],
  incoming: AssistantMessage
): AssistantMessage[] {
  const idx = list.findIndex(
    (m) =>
      (incoming.external_id && m.external_id === incoming.external_id) ||
      (incoming.client_message_id &&
        m.client_message_id === incoming.client_message_id)
  );
  let next: AssistantMessage[];
  if (idx >= 0) {
    next = [...list];
    next[idx] = mergeMessage(list[idx], incoming);
  } else {
    next = [...list, incoming];
  }
  return next.sort(sortBySeq);
}

function updateById(
  list: AssistantMessage[],
  messageId: string | undefined,
  fn: (m: AssistantMessage) => AssistantMessage,
  createIfMissing = false
): AssistantMessage[] {
  // If no id, target the last assistant message (streaming turn).
  let idx = -1;
  if (messageId) {
    idx = list.findIndex((m) => m.external_id === messageId);
  }
  if (idx < 0 && !messageId) {
    // id-less updates (action_confirmation_required / view_command) target the
    // current turn: prefer the latest RUNNING/PENDING assistant so a completed
    // turn isn't retargeted while one is active; fall back to the latest
    // assistant (e.g. an on-completion view_command lands on the just-finished
    // turn).
    let fallback = -1;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].role !== 'ASSISTANT') continue;
      if (list[i].status === 'RUNNING' || list[i].status === 'PENDING') {
        idx = i;
        break;
      }
      if (fallback < 0) fallback = i;
    }
    if (idx < 0) idx = fallback;
  }
  if (idx < 0) {
    if (!createIfMissing || !messageId) return list;
    // Frame arrived before the assistant message was materialised — create a stub.
    const stub: AssistantMessage = {
      external_id: messageId,
      role: 'ASSISTANT',
      content: '',
      status: 'RUNNING',
      seq: maxRealSeq(list) + 1,
      tool_calls: [],
    };
    return [...list, fn(stub)].sort(sortBySeq);
  }
  const next = [...list];
  next[idx] = fn(list[idx]);
  return next;
}

export function reducer(state: State, a: Action): State {
  switch (a.t) {
    case 'reset':
      return { messages: [], turnActive: false };
    case 'setMessages': {
      // preserve not-yet-sent queued optimistic user messages
      const queued = state.messages.filter((m) => m.queued);
      return {
        ...state,
        messages: [...a.messages].sort(sortBySeq).concat(queued),
      };
    }
    case 'upsert':
      return { ...state, messages: upsert(state.messages, a.message) };
    case 'addUser': {
      // Place an optimistic user message just after the last real message so it
      // stays chronological (its POST response is empty, so it is never
      // re-seq'd from the server). Queued messages keep the same rule.
      const seq =
        a.message.seq === OPTIMISTIC_SEQ
          ? maxRealSeq(state.messages) + 1
          : a.message.seq;
      return {
        ...state,
        messages: [...state.messages, { ...a.message, seq }].sort(sortBySeq),
      };
    }
    case 'markQueuedSent':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.client_message_id === a.clientMessageId
            ? { ...m, queued: false }
            : m
        ),
      };
    case 'removeByClientId':
      return {
        ...state,
        messages: state.messages.filter(
          (m) => m.client_message_id !== a.clientMessageId
        ),
      };
    case 'delta':
      return {
        ...state,
        messages: updateById(
          state.messages,
          a.messageId,
          (m) => ({
            ...m,
            content: (m.content || '') + a.delta,
            streaming: true,
            status: m.status === 'PENDING' ? 'RUNNING' : m.status || 'RUNNING',
          }),
          true
        ),
      };
    case 'complete':
      return {
        ...state,
        messages: updateById(state.messages, a.messageId, (m) => ({
          ...m,
          streaming: false,
          status: a.status ?? 'COMPLETED',
        })),
      };
    case 'fail':
      return {
        ...state,
        messages: updateById(state.messages, a.messageId, (m) => ({
          ...m,
          streaming: false,
          status: 'FAILED',
          error_detail: a.error ?? m.error_detail,
        })),
      };
    case 'toolCall':
      return {
        ...state,
        messages: updateById(
          state.messages,
          a.messageId,
          (m) => {
            const i = m.tool_calls.findIndex((c) => c.id === a.toolCall.id);
            const tool_calls = [...m.tool_calls];
            if (i >= 0) tool_calls[i] = { ...tool_calls[i], ...a.toolCall };
            else tool_calls.push(a.toolCall);
            return { ...m, tool_calls };
          },
          true
        ),
      };
    case 'question':
      return {
        ...state,
        messages: updateById(
          state.messages,
          a.messageId,
          (m) => ({ ...m, question: a.question }),
          true
        ),
      };
    case 'action':
      // Append the proposed action (dedupe by external_id) — a turn can propose
      // more than one. Record the split point so text streamed after the first
      // card renders below it (text before stays above).
      return {
        ...state,
        messages: updateById(
          state.messages,
          a.messageId,
          (m) => {
            const existing = m.actions ?? [];
            const actions = existing.some(
              (x) => x.external_id === a.action.external_id
            )
              ? existing.map((x) =>
                  x.external_id === a.action.external_id ? a.action : x
                )
              : [...existing, a.action];
            return {
              ...m,
              actions,
              actionContentLength: m.actionContentLength ?? m.content.length,
            };
          },
          true
        ),
      };
    case 'viewCommand':
      return {
        ...state,
        messages: updateById(
          state.messages,
          a.messageId,
          (m) => ({ ...m, view_command: a.command }),
          true
        ),
      };
    case 'actionDecided':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.actions?.some((x) => x.external_id === a.actionId)
            ? {
                ...m,
                actions: m.actions.map((x) =>
                  x.external_id === a.actionId
                    ? { ...x, decision: a.decision }
                    : x
                ),
              }
            : m
        ),
      };
    case 'questionAnswered':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.external_id === a.messageId && m.question
            ? {
                ...m,
                question: {
                  ...m.question,
                  selected_options: a.selected,
                  answered_by: a.answeredBy ?? m.question.answered_by ?? 'user',
                },
              }
            : m
        ),
      };
    case 'setTurnActive':
      return { ...state, turnActive: a.value };
    default:
      return state;
  }
}
